import { useEffect, useState } from 'react';

import { Provider } from 'react-redux';

import { generateStore } from './store';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from 'react-query';

import config from './config';

import axios from 'axios';
import { PersistProvider } from './context/persistContext';
import { FullScreenLoader } from './components/FullScreenLoader';
import { AppContextProvider } from './context/appContext';
import { NavigationProvider } from './context/NavigationProvider';
import PostHogPageView from './util/pageview';
import { AppRoutes } from './AppRoutes';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ErrorPage } from './components/ErrorPage';
import { KioskInactivityGuard } from './components/Kiosk/KioskInactivityGuard';
import { QuestionnaireConflictDialog } from './components/QuestionnaireConflictDialog';
import { DemoLeadModalGate } from './components/DemoLead/DemoLeadModalGate';

const queryClient = new QueryClient();

export const App = () => {
  const [error, setError] = useState<Error | undefined>();
  // Compute version data synchronously
  const urlParams = new URLSearchParams(window.location.search);

  const queryVersionFromUrl = urlParams.get('version');
  const queryVersionFromStorage = sessionStorage.getItem('queryVersion');
  const queryVersion = queryVersionFromUrl
    ? Number(queryVersionFromUrl)
    : queryVersionFromStorage
    ? Number(queryVersionFromStorage)
    : undefined;
  const envVersion =
    config.kbVersion && !isNaN(config.kbVersion) ? config.kbVersion : undefined;

  const queryVariantSlug =
    urlParams.get('variant')?.replace(/-/g, '_') ?? undefined;

  const [version, setVersion] = useState<number | undefined>(
    !config.env.production ? queryVersion ?? envVersion : undefined,
  );
  const [apiVersion, setApiVersion] = useState<number | undefined>();

  useEffect(() => {
    if (config.env.dev) {
      document.title = '[DEV][PATIENT] - Aldebaran';
    }
    if (config.env.staging) {
      document.title = '[STAGING][PATIENT] - Aldebaran';
    }

    // Fetch API version
    axios
      .get(import.meta.env.VITE_KB_URL + '/version')
      .then((res) => {
        const apiVer = Number(res.data);
        setApiVersion(apiVer);
        if (!version) {
          setVersion(apiVer);
        }
      })
      .catch((err) => {
        console.error(err);
        setError(err);
      });
  }, [version]);

  // Update sessionStorage when versions change
  useEffect(() => {
    if (
      queryVersion &&
      !isNaN(queryVersion) &&
      sessionStorage.getItem('queryVersion') !== String(queryVersion)
    ) {
      sessionStorage.setItem('queryVersion', String(queryVersion));
    }
  }, [queryVersion]);

  if (version && apiVersion && !isNaN(version)) {
    const { store, persistor } = generateStore(
      version,
      {
        current: version,
        query: queryVersion,
        env: envVersion,
        api: apiVersion,
      },
      queryVariantSlug,
    );
    return (
      <Provider store={store}>
        <AppContextProvider>
          <PersistGate loading={null} persistor={persistor}>
            <PersistProvider value={persistor}>
              <QueryClientProvider client={queryClient}>
                <ErrorBoundary
                  fallback={<ErrorPage resetOptions={{ clearStorage: true }} />}
                >
                  <NavigationProvider>
                    <AppRoutes />
                    <DemoLeadModalGate />
                    <KioskInactivityGuard />
                    <QuestionnaireConflictDialog />
                    <PostHogPageView />
                  </NavigationProvider>
                </ErrorBoundary>
              </QueryClientProvider>
            </PersistProvider>
          </PersistGate>
        </AppContextProvider>
      </Provider>
    );
  }
  return (
    <FullScreenLoader error={error ? 'Une erreur est survenue' : undefined} />
  );
};
